import { useCallback, useRef, useState } from "react";

import { useClassNames } from "@/utils/classNames";
import { useDragHandler } from "@/utils/hooks/useDragHandler";
import { clamp } from "@/utils/MathHelper";

import { SliderProps } from "./types";
import styles from "./Slider.module.scss";

/**
 * Slider component - a reusable horizontal slider with draggable thumb
 *
 * Features:
 * - Touch and mouse support
 * - Optional icon display
 * - Customizable min/max range
 * - Accessible (ARIA attributes)
 *
 * @param value - Current value (0-100 by default)
 * @param onChange - Callback when value changes
 * @param icon - Optional icon to display before the slider
 * @param ariaLabel - Optional aria label
 * @param disabled - Optional disabled state
 * @param onDragStart - Optional callback when dragging starts
 * @param onDragEnd - Optional callback when dragging ends
 * @param min - Optional minimum value (default: 0)
 * @param max - Optional maximum value (default: 100)
 * @param className - Additional CSS class for styling
 *
 * @returns The Slider component
 *
 * @example
 * ```tsx
 * <Slider
 *   value={brightness}
 *   onChange={setBrightness}
 *   icon={<Icon icon={StreamingIcons.Settings} />}
 *   ariaLabel="Brightness"
 * />
 * ```
 */
const Slider = ({
  value,
  onChange,
  icon,
  ariaLabel = "Slider",
  disabled = false,
  onDragStart: externalOnDragStart,
  onDragEnd: externalOnDragEnd,
  min = 0,
  max = 100,
  className,
}: SliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Calculate value from position along the slider
   * Handles both landscape and portrait orientations
   * In portrait mode, the container is rotated 90deg, so we need to use clientY instead of clientX
   */
  const calculateValueFromPosition = useCallback(
    (clientX: number, clientY: number): number => {
      if (!sliderRef.current) return value;

      const rect = sliderRef.current.getBoundingClientRect();

      /** Detect if we're in portrait mode by checking if viewport height is greater than width */
      const isPortrait = window.innerHeight > window.innerWidth;

      /** In portrait mode, the container is rotated 90deg, so use clientY for horizontal position */
      const clientPosition = isPortrait ? clientY : clientX;
      const rectStart = isPortrait ? rect.top : rect.left;
      const rectSize = isPortrait ? rect.height : rect.width;

      const position = clamp(clientPosition - rectStart, 0, rectSize);
      const percentage = (position / rectSize) * 100;
      const rawValue = (percentage / 100) * (max - min) + min;
      return Math.round(rawValue);
    },
    [value, min, max],
  );

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      setIsDragging(true);
      const newValue = calculateValueFromPosition(clientX, clientY);
      onChange(newValue);
      externalOnDragStart?.();
    },
    [calculateValueFromPosition, onChange, externalOnDragStart],
  );

  /**
   * Handle drag move
   */
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      const newValue = calculateValueFromPosition(clientX, clientY);
      onChange(newValue);
    },
    [calculateValueFromPosition, onChange],
  );

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    externalOnDragEnd?.();
  }, [externalOnDragEnd]);

  /** Use drag handler hook */
  const { onMouseDown, onTouchStart } = useDragHandler({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
  });

  const containerCn = useClassNames(
    styles,
    styles["slider"],
    isDragging && styles["dragging"],
    disabled && styles["disabled"],
    className,
  );

  const thumbCn = useClassNames(styles, styles["thumb"]);

  // Calculate percentage for display (normalized to 0-100)
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className={containerCn}>
      {/* Optional icon */}
      {icon && <div className={styles["icon"]}>{icon}</div>}

      {/* Slider track */}
      <div
        ref={sliderRef}
        className={styles["slider-track"]}
        onMouseDown={disabled ? undefined : onMouseDown}
        onTouchStart={disabled ? undefined : onTouchStart}
        role="slider"
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Background track */}
        <div className={styles["background-track"]} />

        {/* Filled progress */}
        <div
          className={styles["filled-progress"]}
          style={{ width: `${percentage}%` }}
        />

        {/* Draggable thumb */}
        <div
          className={thumbCn}
          style={{ left: `${percentage}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export { Slider };
